<template>
	<div class="market-level-map" data-testid="market-level-map">
		<div ref="mapDiv" class="map" data-testid="map"></div>
		<div
			ref="mapCards"
			class="map-cards mt-3"
			:class="{ 'justify-content-center': !scrollCards }"
			data-testid="map-cards"
		>
			<div
				v-for="club in clubsData"
				:key="club.mmsClubId"
				:id="`location-${club.mmsClubId}`"
				class="map-card"
				:class="{ selected: club.selected }"
				data-testid="map-card"
				@click="selectClub(club)"
			>
				<div class="d-flex">
					<picture>
						<source media="(min-width:1px)" :srcset="findImage(club, '1x1')?.path" />
						<img
							:alt="findImage(club, '16x9')?.alt ?? `${club.marketingClubName} Exterior`"
							:src="findImage(club, '16x9')?.path"
							class="rounded-3"
						/>
					</picture>

					<div class="ms-1">
						<p class="caption text-uppercase mb-1">{{ club.marketingClubName }}</p>
						<div v-if="club.comingSoon">
							<span class="badge rounded-pill rounded-pill-sm border">{{ club.comingSoon }}</span>
						</div>
						<div v-if="club.locationMessage" data-testid="location-message">
							<span class="badge rounded-pill rounded-pill-sm border">
								{{ club.locationMessage }}
							</span>
						</div>
					</div>
				</div>
				<div class="d-flex justify-content-between align-items-baseline">
					<div class="h5 me-3 mb-0 opacity-75">
						<div v-if="club.joinLink && !club.hideStartingAtPriceLink" class="small opacity-75">
							<a :href="club.joinLink" class="join-link" data-testid="oms-link">
								<span
									v-if="!club.startingAtPriceLinkOverrideText && club.webPreSaleWaitlist === '0'"
									data-testid="startAtPrice"
								>
									<span class="visually-hidden">{{ club.marketingClubName }}</span>
									Starting at {{ club.startingPriceString }}<span aria-hidden="true">/mo</span>
									<span class="visually-hidden">per month</span>
								</span>
								<span v-else>
									<span data-testid="join-text">{{ club.joinLinkCta }}</span>
									<span class="visually-hidden">at {{ club.marketingClubName }}</span>
								</span>
							</a>
							<a
								v-if="club.showSurchargeDisclaimer"
								class="disclaimer-link"
								href="#surcharge-disclaimer"
								aria-label="Surcharge disclaimer"
								aria-describedby="surcharge-disclaimer"
								data-testid="disclaimer-link"
							>
								&ast;
							</a>
						</div>
					</div>
					<a
						v-if="club.showDetailsCta"
						:href="club.prospectPagePath"
						class="btn btn-dark btn-sm position-relative"
						data-testid="details-link"
					>
						Details <span class="visually-hidden">about {{ club.marketingClubName }}</span>
					</a>
				</div>
			</div>
		</div>
	</div>
	<p
		v-if="showDisclaimer"
		id="surcharge-disclaimer"
		class="small"
		data-testid="surcharge-disclaimer"
	>
		&ast; Surcharge laws apply.
	</p>
</template>

<script lang="ts" setup>
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useMapsLoader } from '@/composables/use-maps-loader';
import { mapConfig } from 'Lib/components/find-a-location/util/mapConfig';
import { getAuthoredContent } from '@/util/getAuthoredContent';
import { getClubs } from '@/util/clubs';
import { findImage, joinLink, joinLinkText, showSurchargeDisclaimer } from '@/util/location';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';
import type { MarketLevelMapContent } from 'Types/aemui/market-level-map/aemuiMarketLevelMap';

interface MarketLevelClub extends ltFramework.IClub {
	hideStartingAtPriceLink?: boolean;
	marker?: google.maps.marker.AdvancedMarkerElement;
	position?: google.maps.LatLng;
	selected: boolean;
	joinLink: string;
	joinLinkCta: string;
	showDetailsCta: boolean;
	showSurchargeDisclaimer: boolean;
	startingAtPriceLinkOverrideText?: string;
	isSurchargeIncluded: boolean;
	surchargePercentage: number;
}

const authoredContent: Ref<MarketLevelMapContent> = ref({});
const clubIds = ref<number[]>([]);
const clubsData = ref<MarketLevelClub[]>([]);
let map: google.maps.Map;
const mapDiv = ref<HTMLElement>();
const mapCards = ref<HTMLElement>();

let CoreLibrary: google.maps.CoreLibrary;
let MapLibrary: google.maps.MapsLibrary;
let MarkerLibrary: google.maps.MarkerLibrary;

const getClubIds = (clubsString?: string) => {
	const ids = clubsString
		?.split(',')
		.map((clubId) => Number.parseInt(clubId, 10))
		.filter((clubId) => !Number.isNaN(clubId));
	if (!ids || ids.length === 0) {
		return;
	}
	clubIds.value = ids;
	return ids;
};

const getClubsData = async (): Promise<MarketLevelClub[] | undefined> => {
	try {
		const response = (await getClubs()) as MarketLevelClub[];
		const output = response
			.filter((club) => clubIds.value.includes(club.mmsClubId))
			.sort((a, b) => {
				if (!a.marketingClubName || !b.marketingClubName) {
					return 0;
				} else if (a.marketingClubName > b.marketingClubName) {
					return 1;
				} else if (a.marketingClubName < b.marketingClubName) {
					return -1;
				}
				return 0;
			})
			.map((club) => {
				const joinLinkCta = joinLinkText(club);

				return {
					...club,
					joinLink: club?.hideStartingAtPriceLink ? '' : joinLink(club),
					joinLinkCta,
					selected: false,
					showDetailsCta:
						Boolean(club.prospectPagePath) && authoredContent.value?.hideDetailsCta !== 'true',
					showSurchargeDisclaimer: showSurchargeDisclaimer(club)
				};
			});
		clubsData.value = output;
		return output;
	} catch {
		return;
	}
};

const scrollCards = ref(false);

const checkCardScroll = () => {
	const parentWidth = mapCards.value?.clientWidth ?? 0;
	const children = mapCards.value?.children;
	if (!children) {
		return;
	}
	const childrenWidth = Array.from(children).reduce((accumulator, child) => {
		return child.clientWidth + accumulator;
	}, 0);
	scrollCards.value = parentWidth < childrenWidth;
};

const debouncedCheckCardScroll = useDebounceFn(() => checkCardScroll(), 50, { maxWait: 200 });

useEventListener(window, 'resize', debouncedCheckCardScroll);

const { importLibrary } = useMapsLoader();

onMounted(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	authoredContent.value = getAuthoredContent<MarketLevelMapContent>('aemuiMarketLevelMapScript');
	const ids = getClubIds(authoredContent.value.clubs);
	if (ids) {
		await getClubsData();
	}
	checkCardScroll();

	const [core, maps, marker] = await Promise.all([
		importLibrary('core'),
		importLibrary('maps'),
		importLibrary('marker')
	]);
	CoreLibrary = core;
	MapLibrary = maps;
	MarkerLibrary = marker;

	renderMap();
});

const createIcon = () => {
	const icon = document.createElement('div');
	icon.classList.add('marker');
	return icon;
};

const renderMap = () => {
	map = new MapLibrary.Map(mapDiv.value as HTMLElement, mapConfig(CoreLibrary));
	const bounds = new CoreLibrary.LatLngBounds();

	clubsData.value.forEach((club, index) => {
		const latLng = new CoreLibrary.LatLng(club.latitude!, club.longitude!);
		bounds.extend(latLng);
		const marker = new MarkerLibrary.AdvancedMarkerElement({
			position: latLng,
			map,
			content: createIcon()
		});
		marker.addListener('gmp-click', () => selectClub(club));
		clubsData.value[index]!.marker = marker;
		clubsData.value[index]!.position = latLng;
	});

	map.fitBounds(bounds);
	map.addListener('click', () => selectClub());
};

const selectClub = (clubToSelect?: MarketLevelClub) => {
	clubsData.value.forEach((club, index) => {
		club.selected = false;
		club.marker?.querySelector('.marker')?.classList.remove('selected');
		if (club.mmsClubId === clubToSelect?.mmsClubId) {
			club.selected = true;
			club.marker?.querySelector('.marker')?.classList.add('selected');
			const selected = document.querySelector(`#location-${club.mmsClubId}`);
			const width = selected?.clientWidth ?? 1;
			mapCards.value?.scrollTo({
				left: width * index,
				behavior: 'smooth'
			});
		}
	});
};

const showDisclaimer = computed(() => clubsData.value.some((c) => c.isSurchargeIncluded));

defineExpose({
	clubsData,
	clubIds,
	mapCards,
	scrollCards,
	showDisclaimer,

	checkCardScroll,
	selectClub
});
</script>

<style lang="scss">
.market-level-map {
	position: relative;
	height: 100vh;
	height: 100dvh;
	padding: 2rem 0;
	overflow: hidden;

	@include media-breakpoint-up(md) {
		height: 58rem;
		max-height: 80vh;
		max-height: 80dvh;
	}

	.map {
		height: calc(100% - 9rem);
		width: 100%;
	}

	.marker {
		background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 42 42"><rect width="40" height="40" x="1" y="1" fill="%235C7BAF" rx="20"/><rect width="40" height="40" x="1" y="1" stroke="%23FBF7F2" stroke-width="2" rx="20"/><g clip-path="url(%23a)"><path fill="%23FBF7F2" d="M11.6519 17.6229h3.1562v3.9282h7.5264v2.8261H11.6519v-6.7543Zm7.6405 1.4854c0-.8786.7136-1.5819 1.6226-1.5819.9091 0 1.6201.7033 1.6201 1.5819s-.7136 1.5921-1.6201 1.5921c-.9065 0-1.62-.7033-1.6226-1.5921Zm3.9079 0c0-.8786.7136-1.5819 1.6201-1.5819.9065 0 1.62.7033 1.62 1.5819s-.7135 1.5921-1.62 1.5921c-.9065 0-1.6201-.7033-1.6201-1.5921Zm0 3.7733c0-.8785.7136-1.5819 1.6201-1.5819.9065 0 1.62.7034 1.62 1.5819 0 .8786-.7135 1.5921-1.62 1.5921-.9065 0-1.6201-.7033-1.6201-1.5921Zm3.9079-3.7733c0-.8786.7136-1.5819 1.6201-1.5819.9065 0 1.62.7033 1.62 1.5819s-.7135 1.5921-1.62 1.5921c-.9065 0-1.6201-.7033-1.6201-1.5921Z"/></g><defs><clipPath id="a"><path fill="%23fff" d="M11.6519 10h18.6964v22H11.6519z"/></clipPath></defs></svg>');
		width: 42px;
		height: 42px;

		&.selected,
		&:hover {
			background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 70 70"><rect width="65" height="65" x="2.5" y="2.5" fill="%235C7BAF" rx="32.5"/><rect width="65" height="65" x="2.5" y="2.5" stroke="%23FBF7F2" stroke-width="5" rx="32.5"/><g clip-path="url(%23a)"><path fill="%23FBF7F2" d="M20.9777 29.9342h4.7344v5.8923h11.2895v4.2392H20.9777V29.9342Zm11.4608 2.2282c0-1.3179 1.0703-2.373 2.4339-2.373s2.4301 1.0551 2.4301 2.373c0 1.3178-1.0703 2.3881-2.4301 2.3881s-2.4301-1.055-2.4339-2.3881Zm5.8619 0c0-1.3179 1.0703-2.373 2.43-2.373 1.3598 0 2.4301 1.0551 2.4301 2.373 0 1.3178-1.0703 2.3881-2.4301 2.3881-1.3597 0-2.43-1.055-2.43-2.3881Zm0 5.6599c0-1.3178 1.0703-2.3729 2.43-2.3729 1.3598 0 2.4301 1.0551 2.4301 2.3729 0 1.3179-1.0703 2.3882-2.4301 2.3882-1.3597 0-2.43-1.0551-2.43-2.3882Zm5.8618-5.6599c0-1.3179 1.0703-2.373 2.4301-2.373 1.3597 0 2.43 1.0551 2.43 2.373 0 1.3178-1.0703 2.3881-2.43 2.3881-1.3598 0-2.4301-1.055-2.4301-2.3881Z"/></g><defs><clipPath id="a"><path fill="%23fff" d="M20.9777 18.5h28.0447v33H20.9777z"/></clipPath></defs></svg>');
			height: 70px;
			width: 70px;
			transform: translateY(14px);
		}
	}

	.map-cards {
		display: flex;
		gap: 1.5rem;
		overflow: auto;
		scroll-snap-type: x mandatory;
		scrollbar-color: var(--kpl-border-color-translucent) transparent;
		scrollbar-width: thin;

		.map-card {
			scroll-snap-align: start;
			flex-shrink: 0;
			padding: 0.5rem;
			width: calc(100% - 4rem);

			display: flex;
			flex-direction: column;
			justify-content: space-between;

			border-radius: 6px;
			border: 1px solid rgba(49, 49, 49, 0.15);
			background: var(--kpl-light);
			cursor: pointer;

			@include media-breakpoint-up(md) {
				width: 19rem;
			}

			&.selected,
			&:hover {
				background-color: var(--kpl-contrast-200);
			}

			&.selected {
				outline: 4px solid var(--kpl-off-white);
				outline-offset: -4px;
			}

			img {
				width: 80px;
			}
		}
	}

	.join-link,
	.disclaimer-link {
		display: inline-block;
		height: 24px;
		line-height: 24px;
	}

	.disclaimer-link {
		width: 24px;
	}
}
</style>
